'use client';
import { CombinedGraphQLErrors } from '@apollo/client';
import { usePathname, useRouter } from 'next/navigation';
import { ReactNode, useEffect, useMemo, Fragment } from 'react';

import LoadingMessage from 'components/Loading';
import { FullscreenCenter } from 'components/shared-styles-v2';
import { UNAUTHORIZED_USER_PATH } from 'config/internalUrlPaths';
import { UserContext, toUserContext } from 'context/Profile/User';
import { SchoolContext } from 'context/School/School';
import { School } from 'model/misc';
import { useTeacherfirstInit } from 'network/graphql/Initialization/teacherfirst';
import useLocalForageState from 'utils/hooks/useLocalForageState';
import { ddlog } from 'utils/miscellaneous';

import { TeacherContext, useUserToTeacherContext } from '../Profile/TeacherProfile';

interface Props {
  children: ReactNode;
}

const InitializeTeacherfirst = ({ children }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const [selectedSchoolId, setSelectedSchoolId, isSchoolLoading] = useLocalForageState('selected_school_id');
  const { data, error, hasRetried, refetch } = useTeacherfirstInit(isSchoolLoading);

  const uniqueSchools = useMemo(() => {
    const schools = new Map<string, School>();
    data?.user.teacherProfiles.forEach((teacherProfile) => {
      const teacherSchool = teacherProfile.department?.school;
      if (teacherSchool) {
        schools.set(teacherSchool.id, teacherSchool);
      }
    });
    return Array.from(schools.values());
  }, [data?.user.teacherProfiles]);

  const school = useMemo(() => {
    if (!data?.schools) {
      return undefined;
    }
    return data.schools.edges.find((s) => s.id === selectedSchoolId) || uniqueSchools[0];
  }, [data, selectedSchoolId, uniqueSchools]);

  const userContext = useMemo(() => {
    if (!data?.user) {
      return undefined;
    }

    return toUserContext(data.user);
  }, [data]);

  const teacherContext = useUserToTeacherContext({ user: data?.user, refetch });

  const hasNoTeacherProfiles = data?.user?.teacherProfiles.length === 0;
  const hasUnauthorizedUserError =
    CombinedGraphQLErrors.is(error) &&
    error?.errors?.some(
      (err) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        (err.extensions?.response as { status?: number })?.status === 401 ||
        err.message?.includes('401'),
    );

  // We only want to redirect to the unauthorized page after the first retry, to give the app a chance to recover from transient errors.
  // When logging in first time, it takes a while to set user permissions in the backend, which can cause a 401 error on the initial request. Retrying immediately can help in this case.
  const shouldRedirectUnauthorized = hasNoTeacherProfiles || (hasRetried && hasUnauthorizedUserError);

  useEffect(() => {
    if (shouldRedirectUnauthorized) {
      router.push(UNAUTHORIZED_USER_PATH);
    }
  }, [shouldRedirectUnauthorized, router]);

  if (shouldRedirectUnauthorized && pathname?.startsWith(UNAUTHORIZED_USER_PATH)) {
    return <Fragment>{children}</Fragment>;
  }

  if (!userContext || !school || !data?.schools?.edges || !teacherContext?.selectedTeacher) {
    return (
      <FullscreenCenter>
        <LoadingMessage />
      </FullscreenCenter>
    );
  }

  if (error) {
    ddlog.error('failed to initialize teacherfirst', { message: error.message, stack: error.stack }, error);
  }

  return (
    <UserContext.Provider value={userContext}>
      <TeacherContext.Provider value={teacherContext}>
        <SchoolContext.Provider
          value={{
            schoolId: school.id,
            school,
            availableSchools: uniqueSchools.slice().sort((a, b) => a.name.localeCompare(b.name)),
            setSchoolId: setSelectedSchoolId,
          }}
        >
          {children}
        </SchoolContext.Provider>
      </TeacherContext.Provider>
    </UserContext.Provider>
  );
};

export default InitializeTeacherfirst;
